import axios from "axios";
import { useQuery } from "@tanstack/react-query";

export interface LogMessage {
  id: number;
  content: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number | string;
  tool_count: number;
  created_at: string;
  duration: number | null;
  conversation: {
    id: number;
    title: string | null;
  };
  user: {
    id: number;
    email: string;
    name: string | null;
    profile_id: number | null;
    profile_type: string | null;
    profile_class: string | null;
    profile_label: string | null;
  };
}

export interface LogStats {
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
}

export interface LogsResponse {
  messages: LogMessage[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export interface LogConversationMessage {
  id: number;
  template_type: "user" | "tool" | "analyzer" | "synthesizer" | "system";
  status: "pending" | "progress" | "success" | "error" | "interrupted";
  content: string | null;
  parent_id: number | null;
  tool_call_result?: Record<string, unknown>;
  created_at: string;
  start_at?: string;
  end_at?: string;
  model?: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
}

export interface LogConversationDetail {
  id: number;
  title: string | null;
  created_at: string;
  user: {
    id: number;
    email: string;
    name: string | null;
    profile_type: string | null;
    profile_label: string | null;
  };
  messages: LogConversationMessage[];
  usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    cost_usd: number;
  };
}

const BASE_URL = "/system/agentic/logs";

export const logsApi = {
  list: async (page: number = 1): Promise<LogsResponse> => {
    const { data } = await axios.get(`${BASE_URL}.json`, {
      params: { page },
    });
    return data;
  },

  stats: async (): Promise<LogStats> => {
    const { data } = await axios.get(`${BASE_URL}/stats.json`);
    return data;
  },

  detail: async (conversationId: number): Promise<LogConversationDetail> => {
    const { data } = await axios.get(`${BASE_URL}/${conversationId}.json`);
    return data;
  },
};

export const logsKeys = {
  all: ["logs"] as const,
  page: (page: number) => ["logs", page] as const,
  stats: ["logs", "stats"] as const,
  detail: (id: number) => ["logs", "detail", id] as const,
};

export function useLogs(page: number = 1) {
  return useQuery({
    queryKey: logsKeys.page(page),
    queryFn: () => logsApi.list(page),
  });
}

export function useLogConversation(conversationId: number | null) {
  return useQuery({
    queryKey: logsKeys.detail(conversationId!),
    queryFn: () => logsApi.detail(conversationId!),
    enabled: conversationId !== null,
  });
}

export function useLogsStats() {
  return useQuery({
    queryKey: logsKeys.stats,
    queryFn: logsApi.stats,
    staleTime: 1000 * 60, // 1 minute
  });
}
